/**
 * Renders a nav item that wraps children (e.g. "Reports", "Personas").
 * Two presentation modes:
 *   - mobile drawer: inline accordion (children render below the button).
 *   - desktop sidebar: side flyout panel (anchored via useNavFlyout).
 */

import React, { memo } from "react";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";
import { ChevronDown, ChevronRight, Lock } from "lucide-react";

function NavGroupItemImpl({
  item,
  collapsed,
  mobileOpen,
  itemLocked,
  inlineOpen,
  flyoutOpen,
  flyoutPos,
  flyoutRef,
  buttonRef,
  hasExpiredPassports,
  hasExpiredStaffDocs,
  online,
  onToggle,
  onChildNav,
}) {
  const { t } = useTranslation();
  const location = useLocation();
  const GroupIcon = item.icon;
  const childActive = item.children.some((c) => location.pathname === c.path);
  const groupPassportDot = item.children.some((c) => c.path === "/owners") && hasExpiredPassports;
  const groupStaffDot = item.children.some((c) => c.path === "/staff") && hasExpiredStaffDocs;
  const groupDot = groupPassportDot || groupStaffDot;
  const labelCollapsed = collapsed && !mobileOpen;

  return (
    <div className="relative">
      <button
        ref={buttonRef}
        data-testid={`nav-group-${item.groupKey}`}
        title={itemLocked ? t("offline.requiresConnection") : undefined}
        onClick={() => {
          if (itemLocked) return;
          onToggle();
        }}
        className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-colors duration-200
          ${
            itemLocked
              ? "text-slate-600 cursor-not-allowed opacity-60"
              : flyoutOpen
                ? "text-white bg-slate-800/60"
                : childActive
                  ? "text-white hover:bg-slate-800/50"
                  : "text-slate-400 hover:text-white hover:bg-slate-800/50"
          }
        `}
      >
        <div className="relative flex-shrink-0">
          <GroupIcon className="w-[18px] h-[18px]" />
          {groupDot && labelCollapsed && (
            <span className="absolute -top-1 -right-1 w-[7px] h-[7px] bg-red-500 rounded-full" />
          )}
        </div>
        {labelCollapsed && (
          <ChevronRight className="w-3 h-3 flex-shrink-0 ml-auto -mr-1 opacity-60" />
        )}
        {!labelCollapsed && (
          <>
            <span className="flex-1 text-left">{t(item.labelKey)}</span>
            {groupDot && !inlineOpen && !flyoutOpen && (
              <span className="w-2 h-2 bg-red-500 rounded-full flex-shrink-0" />
            )}
            {itemLocked ? (
              <Lock className="w-3.5 h-3.5 flex-shrink-0 opacity-70" />
            ) : mobileOpen ? (
              inlineOpen ? (
                <ChevronDown className="w-4 h-4 flex-shrink-0" />
              ) : (
                <ChevronRight className="w-4 h-4 flex-shrink-0" />
              )
            ) : (
              <ChevronRight className="w-4 h-4 flex-shrink-0" />
            )}
          </>
        )}
      </button>

      {/* Mobile inline accordion */}
      {inlineOpen && (
        <div className="mt-1 ml-3 pl-3 border-l border-slate-800 space-y-1">
          {item.children.map((child) => (
            <ChildButton
              key={child.path}
              child={child}
              variant="inline"
              online={online}
              hasExpiredPassports={hasExpiredPassports}
              hasExpiredStaffDocs={hasExpiredStaffDocs}
              onClick={() => onChildNav(child)}
            />
          ))}
        </div>
      )}

      {/* Desktop side flyout — fixed positioning so it escapes the
          nav's overflow clipping (overflow-y:auto forces overflow-x
          to clip too, which previously hid this panel). */}
      {flyoutOpen && flyoutPos && (
        <div
          ref={flyoutRef}
          style={{
            transformOrigin: "left center",
            position: "fixed",
            top: flyoutPos.top,
            left: flyoutPos.left,
          }}
          className="w-56 bg-[#0f172a] border border-slate-700 rounded-xl shadow-2xl py-2 px-2 space-y-1 z-[70] animate-flyout-in"
        >
          <p className="text-[10px] uppercase tracking-wider text-slate-400 px-3 pb-1">
            {t(item.labelKey)}
          </p>
          {item.children.map((child) => (
            <ChildButton
              key={child.path}
              child={child}
              variant="flyout"
              online={online}
              hasExpiredPassports={hasExpiredPassports}
              hasExpiredStaffDocs={hasExpiredStaffDocs}
              onClick={() => onChildNav(child)}
            />
          ))}
        </div>
      )}
    </div>
  );
}

const NavGroupItem = memo(NavGroupItemImpl);
export default NavGroupItem;

function ChildButton({
  child,
  variant,
  online,
  hasExpiredPassports,
  hasExpiredStaffDocs,
  onClick,
}) {
  const { t } = useTranslation();
  const location = useLocation();
  const ChildIcon = child.icon;
  const active = location.pathname === child.path;
  const showPassportDot = child.path === "/owners" && hasExpiredPassports;
  const showStaffDot = child.path === "/staff" && hasExpiredStaffDocs;
  const childLocked = !online && !child.offlineAvailable;
  // Inline (mobile drawer) uses slightly larger pills; flyout uses tighter
  // rounded-lg corners. Otherwise the markup is identical.
  const radius = variant === "flyout" ? "rounded-lg" : "rounded-xl";
  const hover = variant === "flyout" ? "hover:bg-slate-800/60" : "hover:bg-slate-800/50";

  return (
    <button
      data-testid={`nav-${child.path.replace("/", "")}`}
      onClick={() => {
        if (!childLocked) onClick();
      }}
      title={childLocked ? t("offline.requiresConnection") : undefined}
      className={`w-full flex items-center gap-3 px-3 py-2 ${radius} text-sm font-medium transition-colors duration-200
        ${
          childLocked
            ? "text-slate-600 cursor-not-allowed opacity-60"
            : active
              ? "bg-altiv-navy/20 text-white border border-altiv-navy/40"
              : `text-slate-400 hover:text-white ${hover}`
        }
      `}
    >
      <ChildIcon className="w-[16px] h-[16px] flex-shrink-0" />
      <span className="flex-1 text-left">{t(child.labelKey)}</span>
      {childLocked && <Lock className="w-3.5 h-3.5 flex-shrink-0 opacity-70" />}
      {(showPassportDot || showStaffDot) && !childLocked && (
        <span className="w-2 h-2 bg-red-500 rounded-full flex-shrink-0" />
      )}
    </button>
  );
}
